import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../components/AppShell";
import PageHero from "../components/PageHero";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";
import {
  CreditCard, ArrowLeft, ExternalLink, Check, X, ArrowUp, ArrowDown, Sparkles,
  Download, RefreshCw, Loader2, ShieldCheck, AlertTriangle, RotateCcw, Ban,
} from "lucide-react";

function fmtDate(sec) {
  if (!sec) return "—";
  try { return new Date(sec * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return "—"; }
}

function fmtMoney(amount, currency = "usd") {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: (currency || "usd").toUpperCase() }).format((amount || 0) / 100);
  } catch { return `$${((amount || 0) / 100).toFixed(2)}`; }
}

const STATUS_TONE = {
  active:   { label: "Active",           tone: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  trialing: { label: "In trial",         tone: "bg-sky-100 text-sky-800 border-sky-200" },
  past_due: { label: "Past due",         tone: "bg-pink-100 text-pink-900 border-pink-200" },
  canceled: { label: "Cancelled",        tone: "bg-rose-100 text-rose-800 border-rose-200" },
  incomplete: { label: "Incomplete",     tone: "bg-pink-100 text-pink-900 border-pink-200" },
  unpaid:   { label: "Unpaid",           tone: "bg-rose-100 text-rose-800 border-rose-200" },
  free:     { label: "Free tier",        tone: "bg-shell-muted text-muted-foreground border-shell-border" },
};

export default function AccountSubscription() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [portalBusy, setPortalBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [interval, setInterval_] = useState("monthly");

  const load = async () => {
    setLoading(true);
    try {
      const [subRes, pkgRes, invRes] = await Promise.all([
        api.get("/billing/subscription"),
        api.get("/billing/packages"),
        api.get("/billing/invoices").catch(() => ({ data: { invoices: [] } })),
      ]);
      setSub(subRes.data);
      setPkgs(pkgRes.data || []);
      setInvoices(invRes.data.invoices || []);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/portal", { return_url: origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatError(err));
      setPortalBusy(false);
    }
  };

  const doCancel = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Cancellation scheduled — access continues until the end of the current billing period.");
      setConfirmCancel(false);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCancelBusy(false);
    }
  };

  const doReactivate = async () => {
    setCancelBusy(true);
    try {
      await api.post("/billing/reactivate");
      toast.success("Cancellation reversed — your plan will continue to renew.");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setCancelBusy(false);
    }
  };

  const startCheckout = async (packageSlug) => {
    // Founder → Pro upgrade discount: when a Founder-tier user clicks
    // "Upgrade" on the Founder Pro card in the plan grid, remap the
    // target slug to the discounted upgrade virtual slug so they only
    // pay the $500 delta (or $1 for the QA twin) instead of the full
    // $1,000 sticker price. Backend resolves the virtual slug to the
    // upgrade Stripe price and still grants the underlying paid plan
    // (`pro` / `qa_pro`) on success. Mirrors the "Upgrade for $500"
    // card above so both entry points charge the same amount.
    const current = sub?.package_slug;
    let effectiveSlug = packageSlug;
    if (current === "founder" && packageSlug === "pro") {
      effectiveSlug = "founder_pro_upgrade";
    } else if (current === "qa_founder" && packageSlug === "qa_pro") {
      effectiveSlug = "founder_pro_qa_upgrade";
    }
    setCheckoutBusy(packageSlug);
    try {
      const { data } = await api.post("/payments/checkout", {
        package_slug: effectiveSlug, interval, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatError(err));
      setCheckoutBusy(null);
    }
  };

  const statusMeta = STATUS_TONE[(sub?.stripe_status || sub?.package_status || "free")] || STATUS_TONE.free;

  return (
    <AppShell title="Subscription & Billing">
      <div className="w-full">
        <Link
          to="/account"
          data-testid="account-subscription-back"
          className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-primary transition mb-4"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to account
        </Link>
        <PageHero
          eyebrow="Subscription"
          icon={CreditCard}
          title={<>Subscription & <span className="text-grad">Billing</span></>}
          subtitle="Review your current plan, change tiers, update your card, and download every past invoice."
          testid="account-subscription"
        />

        {loading && (
          <div className="surface p-8 text-center text-muted-foreground" data-testid="account-subscription-loading">
            <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading your billing state…
          </div>
        )}

        {!loading && sub && (
          <>
            {/* Look up the founder's plan against the catalog so the
                current-plan card can render a friendly name / tagline
                / "$0 one-time" pill even for the Free tier (previous
                copy dropped everything but the slug for free users so
                the card read as "No active plan"). Falls back to the
                raw slug when the catalog hasn't loaded yet. */}
            {(() => null)()}
            {/* ── Current plan card ─────────────────────────────────── */}
            <section className="surface p-6" data-testid="subscription-current-card">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Current plan</p>
                  <div className="flex items-center gap-3 mt-2">
                    <h2 className="font-display text-3xl" data-testid="subscription-plan-name">
                      {(() => {
                        const slug = sub.package_slug || "free";
                        const pkg = pkgs.find((p) => p.slug === slug);
                        return pkg?.name || slug.toUpperCase();
                      })()}
                    </h2>
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ${statusMeta.tone}`}
                          data-testid="subscription-status-chip">
                      {statusMeta.label}
                    </span>
                    {sub.cancel_at_period_end && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-pink-200 bg-pink-50 text-pink-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]"
                            data-testid="subscription-cancel-pending">
                        <AlertTriangle className="w-2.5 h-2.5" /> Ends {fmtDate(sub.current_period_end)}
                      </span>
                    )}
                  </div>
                  {(() => {
                    // Plan description block. Three cases the previous
                    // build silently skipped:
                    //   1. Free tier — no `sub.plan.amount`, so show
                    //      "$0 · Always free" so the founder sees they
                    //      actually have an entitlement.
                    //   2. One-time paid tier — no renewal, no
                    //      interval; render the sticker price + the
                    //      "one-time" pill instead of "$500 per month".
                    //   3. Legacy recurring subscription — keep the
                    //      existing per-interval / renewal copy.
                    const slug = sub.package_slug || "free";
                    if (slug === "free" || slug === "qa_free") {
                      const pkg = pkgs.find((p) => p.slug === slug);
                      return (
                        <p className="mt-3 text-sm text-muted-foreground">
                          <b className="text-foreground text-lg font-display">$0</b>
                          {" "}· Always free{pkg?.tagline ? <> · {pkg.tagline}</> : null}
                        </p>
                      );
                    }
                    const pkg = pkgs.find((p) => p.slug === slug);
                    const oneTime = !!pkg?.one_time;
                    if (oneTime && pkg?.prices) {
                      const anyPrice = pkg.prices.monthly || pkg.prices.yearly || Object.values(pkg.prices)[0];
                      if (anyPrice?.amount != null) {
                        return (
                          <p className="mt-3 text-sm text-muted-foreground">
                            <b className="text-foreground text-lg font-display">{fmtMoney(anyPrice.amount, anyPrice.currency)}</b>
                            {" "}· One-time payment · Lifetime access
                          </p>
                        );
                      }
                    }
                    if (sub.plan?.amount != null) {
                      return (
                        <p className="mt-3 text-sm text-muted-foreground">
                          <b className="text-foreground text-lg font-display">{fmtMoney(sub.plan.amount, sub.plan.currency)}</b>
                          {" "}per {sub.plan.interval || "month"}
                          {sub.plan.interval_count && sub.plan.interval_count > 1 ? ` × ${sub.plan.interval_count}` : ""}
                          {sub.current_period_end && !sub.cancel_at_period_end && (
                            <> · Renews {fmtDate(sub.current_period_end)}</>
                          )}
                        </p>
                      );
                    }
                    return null;
                  })()}
                  {sub.payment_method && (
                    <p className="mt-1 text-[13px] text-muted-foreground inline-flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5" />
                      {(sub.payment_method.brand || "card").toUpperCase()} ending in {sub.payment_method.last4}
                      {sub.payment_method.exp_month && (
                        <> · exp {String(sub.payment_method.exp_month).padStart(2, "0")}/{String(sub.payment_method.exp_year).slice(-2)}</>
                      )}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={openPortal}
                    disabled={portalBusy || !sub.has_active_subscription}
                    data-testid="subscription-portal-btn"
                    title={sub.has_active_subscription ? "Update card, view details" : "Subscribe first"}
                    className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {portalBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                    Manage in Stripe
                  </button>
                  <button
                    type="button"
                    onClick={load}
                    disabled={loading}
                    data-testid="subscription-refresh-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-shell-border px-3 py-2 text-xs font-semibold hover:border-primary/40 transition"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                  </button>
                </div>
              </div>

              {/* Cancel / Reactivate row */}
              {sub.has_active_subscription && (
                <div className="mt-5 border-t border-shell-border pt-4">
                  {sub.cancel_at_period_end ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="subscription-reactivate-row">
                      <p className="text-sm text-pink-900 inline-flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        Your plan will end on <b>{fmtDate(sub.current_period_end)}</b>. Change your mind?
                      </p>
                      <button
                        type="button"
                        onClick={doReactivate}
                        disabled={cancelBusy}
                        data-testid="subscription-reactivate-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-4 py-2 text-xs font-semibold hover:bg-emerald-500 transition disabled:opacity-40"
                      >
                        {cancelBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Reactivate
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setConfirmCancel(true)}
                      data-testid="subscription-cancel-btn"
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-rose-700 hover:underline"
                    >
                      <Ban className="w-3.5 h-3.5" /> Cancel subscription
                    </button>
                  )}
                </div>
              )}

              {/* Retry Payment — surfaces when the last invoice is
                  unpaid (dunning) so the user can rerun the charge
                  from the Stripe-hosted page without leaving the app.
                  Shows for status past_due / unpaid or whenever an
                  open invoice exists on the current subscription. */}
              {(() => {
                const inv = sub.latest_invoice;
                const invStatus = (inv?.status || "").toLowerCase();
                const subStatus = (sub.stripe_status || "").toLowerCase();
                const showRetry =
                  !!inv?.hosted_invoice_url && (
                    invStatus === "open" || invStatus === "uncollectible" ||
                    subStatus === "past_due" || subStatus === "unpaid"
                  );
                if (!showRetry) return null;
                const amount = fmtMoney(inv.amount_paid || inv.amount_due || inv.amount_remaining, sub.plan?.currency);
                return (
                  <div className="mt-4 rounded-xl border border-rose-300 bg-rose-50 p-4" data-testid="subscription-retry-panel">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-rose-800 text-sm inline-flex items-center gap-1.5">
                          <AlertTriangle className="w-3.5 h-3.5" /> Payment failed
                        </p>
                        <p className="text-[13px] text-rose-800/90 mt-1">
                          Your last invoice ({amount}) could not be charged. Retry now to keep
                          your subscription active — your saved payment method will be reused
                          for future renewals.
                        </p>
                      </div>
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="subscription-retry-btn"
                        className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-4 py-2 text-xs font-semibold hover:bg-rose-500 transition"
                      >
                        <RotateCcw className="w-3.5 h-3.5" /> Retry Payment
                      </a>
                    </div>
                  </div>
                );
              })()}
            </section>

            {/* ── Founder → Pro upgrade card ───────────────────────
              * Only visible to users currently on the Founder plan (real
              * or QA). Charges the $500 delta ($1 for QA) via the
              * `founder_pro_upgrade` virtual slug — the backend resolves
              * it to the discounted Stripe price but grants the same
              * `pro` / `qa_pro` plan on success, so entitlements /
              * menu-directory gates stay in the same taxonomy. */}
            {["founder", "qa_founder"].includes(sub.package_slug) && (() => {
              const isQa = sub.package_slug === "qa_founder";
              const upgradeSlug = isQa ? "founder_pro_qa_upgrade" : "founder_pro_upgrade";
              const upgradePriceLabel = isQa ? "$1" : "$500";
              const proLabel = isQa ? "Founder Pro QA" : "Founder Pro";
              return (
                <section
                  className="mt-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-white to-fuchsia-50/40 p-6 shadow-sm"
                  data-testid="subscription-upgrade-card"
                >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-primary">Upgrade available</p>
                      <h3 className="mt-1 font-display text-2xl text-neutral-900">Upgrade to {proLabel}</h3>
                      <p className="mt-1 text-sm text-muted-foreground">
                        You already paid for Founder — pay only the {upgradePriceLabel} difference to unlock every {proLabel} feature. One-time payment, no subscription.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => startCheckout(upgradeSlug)}
                      disabled={checkoutBusy === upgradeSlug}
                      data-testid="subscription-upgrade-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-primary text-white px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-40"
                    >
                      {checkoutBusy === upgradeSlug
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <RotateCcw className="w-4 h-4" />}
                      Upgrade for {upgradePriceLabel}
                    </button>
                  </div>
                </section>
              );
            })()}

            {/* ── Upgrade / plan grid ───────────────────────────────── */}
            <section className="mt-8" data-testid="subscription-plans">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">
                  {sub.has_active_subscription ? "Change plan" : "Choose a plan"}
                </h2>
                {/* Monthly / yearly toggle removed — post-Feb 2026 all
                    packages are one-time payments (Founder $500 /
                    Founder Pro $1,000 / QA twins). The catalog no
                    longer exposes distinct monthly vs yearly prices,
                    so the switch had no effect and confused founders.
                    See `startCheckout` for the interval usage. */}
              </div>

              {/* Plan cards — auto-fill minmax so the third card wraps
                  to a second row at tablet widths (768-1279) where 3
                  cards would clip the right edge, then goes back to a
                  clean 3-column layout at xl (1280+). Same pattern the
                  sitewide card-grid fix uses. */}
              <div
                className="grid gap-4"
                style={{ gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))" }}
              >
                {pkgs.map((p) => {
                  // For the internal QA plan the interval toggle
                  // doesn't apply — the price is $1/day. Fall back to
                  // the daily price so testers always see a valid CTA.
                  const price = p.prices?.[interval] || (p.slug === "qa_daily" ? p.prices?.daily : null);
                  // One-time plans render "one-time" instead of "/mo" or "/yr".
                  // Backend PACKAGES tags Founder / Founder Pro / QA variants
                  // with `one_time: true` after the Feb 2026 Klarna migration.
                  const isOneTime = !!p.one_time;
                  const priceUnitLabel = isOneTime
                    ? "one-time"
                    : (p.slug === "qa_daily" ? "day" : (interval === "monthly" ? "mo" : "yr"));
                  const isCurrent = p.is_current;
                  const isUpgrade = p.is_upgrade;
                  const isDowngrade = p.is_downgrade;
                  // Founder → Pro upgrade: show the discounted delta
                  // price ($500 / $1 QA) on the Pro card + change CTA
                  // wording so it matches what Stripe will actually
                  // charge (see `startCheckout` slug remap).
                  const current = sub?.package_slug;
                  const isFounderUpgradingToPro =
                    (current === "founder" && p.slug === "pro") ||
                    (current === "qa_founder" && p.slug === "qa_pro");
                  const upgradeAmount =
                    current === "founder" ? 50000
                    : current === "qa_founder" ? 100
                    : null;
                  const displayPrice = isFounderUpgradingToPro && upgradeAmount != null
                    ? { amount: upgradeAmount, currency: price?.currency || "usd" }
                    : price;
                  return (
                    <div
                      key={p.slug}
                      data-testid={`plan-card-${p.slug}`}
                      className={`rounded-2xl border p-5 flex flex-col ${
                        isCurrent
                          ? "border-primary bg-primary/5"
                          : "border-shell-border bg-white hover:border-primary/40 transition"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <h3 className="font-display text-xl">{p.name}</h3>
                        {isCurrent && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-primary text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
                            <Check className="w-2.5 h-2.5" /> Current
                          </span>
                        )}
                      </div>
                      <p className="text-[12px] text-muted-foreground mt-1">{p.tagline}</p>
                      <div className="mt-4">
                        {displayPrice ? (
                          <>
                            <span className="font-display text-3xl">{fmtMoney(displayPrice.amount, displayPrice.currency)}</span>
                            {(displayPrice.amount || 0) > 0 && (
                              <span className="text-sm text-muted-foreground"> {isOneTime ? priceUnitLabel : `/${priceUnitLabel}`}</span>
                            )}
                            {isFounderUpgradingToPro && (
                              <p className="text-[11px] text-primary font-semibold mt-1" data-testid={`plan-upgrade-delta-${p.slug}`}>
                                Upgrade delta — Founder credit applied
                              </p>
                            )}
                          </>
                        ) : (
                          <span className="text-sm text-muted-foreground italic">Contact sales</span>
                        )}
                      </div>
                      <ul className="mt-4 space-y-1.5 text-[13px] text-foreground flex-1">
                        {(p.features || []).slice(0, 6).map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <Check className="w-3.5 h-3.5 text-primary mt-0.5 shrink-0" /> <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-5">
                        {isCurrent ? (
                          <span className="w-full inline-flex items-center justify-center gap-1 rounded-full border border-shell-border px-3 py-2 text-xs font-semibold text-muted-foreground">
                            You're on this plan
                          </span>
                        ) : isDowngrade && sub.has_active_subscription ? (
                          // The Stripe billing portal is the only sane
                          // path for a recurring-subscription downgrade
                          // (proration + cadence swap). One-time paid
                          // founders (Founder / Founder Pro) never
                          // reach here — the button is hidden and the
                          // downgrade slot renders as a neutral
                          // "Not applicable" chip so the card never
                          // shows a control that would 4xx server-side.
                          <button
                            type="button"
                            onClick={openPortal}
                            disabled={portalBusy}
                            data-testid={`plan-downgrade-${p.slug}`}
                            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full border border-shell-border px-3 py-2 text-xs font-semibold hover:border-primary/40 transition"
                            title="Downgrades go through the Stripe portal so proration works cleanly"
                          >
                            <ArrowDown className="w-3.5 h-3.5" /> Downgrade in Stripe
                          </button>
                        ) : isDowngrade ? (
                          <span
                            className="w-full inline-flex items-center justify-center gap-1 rounded-full border border-shell-border px-3 py-2 text-xs font-semibold text-muted-foreground"
                            data-testid={`plan-downgrade-na-${p.slug}`}
                          >
                            Not applicable
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => startCheckout(p.slug)}
                            disabled={!price || checkoutBusy === p.slug}
                            data-testid={`plan-upgrade-${p.slug}`}
                            className="w-full btn-primary text-xs justify-center inline-flex items-center gap-1.5"
                          >
                            {checkoutBusy === p.slug ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : isUpgrade ? (
                              <ArrowUp className="w-3.5 h-3.5" />
                            ) : (
                              <Sparkles className="w-3.5 h-3.5" />
                            )}
                            {isFounderUpgradingToPro
                              ? `Upgrade for ${fmtMoney(upgradeAmount, displayPrice?.currency || "usd")}`
                              : isUpgrade ? "Upgrade" : "Choose plan"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── Past invoices ─────────────────────────────────────── */}
            <section className="mt-8 surface overflow-hidden" data-testid="subscription-invoices">
              <div className="px-5 py-4 border-b border-shell-border bg-shell-muted/40 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Billing history</p>
                  <h3 className="font-display text-lg mt-0.5">Past invoices</h3>
                </div>
                <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-emerald-600" /> Powered by Stripe
                </p>
              </div>
              {invoices.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground" data-testid="subscription-invoices-empty">
                  No invoices yet. Once your first payment lands, your receipts will appear here.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-shell-muted/30">
                    <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                      <th className="px-5 py-2 font-semibold">Date</th>
                      <th className="px-3 py-2 font-semibold">Invoice #</th>
                      <th className="px-3 py-2 font-semibold text-right">Amount</th>
                      <th className="px-3 py-2 font-semibold">Status</th>
                      <th className="px-3 py-2 font-semibold text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="border-t border-shell-border" data-testid={`invoice-row-${inv.id}`}>
                        <td className="px-5 py-3 whitespace-nowrap text-xs">{fmtDate(inv.created)}</td>
                        <td className="px-3 py-3 text-xs font-mono">{inv.number || inv.id.slice(-8)}</td>
                        <td className="px-3 py-3 text-right text-xs font-semibold">
                          {fmtMoney(inv.amount_paid || inv.amount_due, inv.currency)}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${
                            inv.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                            inv.status === "open" ? "bg-pink-100 text-pink-900 border-pink-200" :
                            "bg-shell-muted text-muted-foreground border-shell-border"
                          }`}>
                            {inv.status || "unknown"}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right space-x-1">
                          {inv.hosted_invoice_url && (
                            <a
                              href={inv.hosted_invoice_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`invoice-view-${inv.id}`}
                              className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition"
                            >
                              <ExternalLink className="w-3 h-3" /> View
                            </a>
                          )}
                          {inv.invoice_pdf && (
                            <a
                              href={inv.invoice_pdf}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`invoice-pdf-${inv.id}`}
                              className="inline-flex items-center gap-1 rounded-full border border-shell-border px-2.5 py-1 text-[11px] font-semibold hover:border-primary/40 transition"
                            >
                              <Download className="w-3 h-3" /> PDF
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}

        {/* ── Cancel confirm modal ─────────────────────────────────── */}
        {confirmCancel && (
          <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" data-testid="cancel-modal">
            <div className="max-w-md w-full rounded-2xl bg-white shadow-2xl p-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-pink-100 text-pink-800 grid place-items-center shrink-0">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-xl">Cancel your subscription?</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    You'll keep full access until <b>{fmtDate(sub?.current_period_end)}</b>, then your account will revert to the free tier. No refund on the current period.
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmCancel(false)}
                  className="inline-flex items-center rounded-full border border-shell-border px-4 py-2 text-xs font-semibold hover:border-primary/40 transition"
                  data-testid="cancel-modal-dismiss"
                >
                  <X className="w-3.5 h-3.5 mr-1" /> Keep my plan
                </button>
                <button
                  type="button"
                  onClick={doCancel}
                  disabled={cancelBusy}
                  data-testid="cancel-modal-confirm"
                  className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 text-white px-4 py-2 text-xs font-semibold hover:bg-rose-500 transition disabled:opacity-40"
                >
                  {cancelBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />}
                  Cancel anyway
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
